import { FotoIntervento } from 'api'
import { last, memoize, orderBy } from 'lodash-es'

import { fetchWithTimeout, UPLOAD_TIMEOUT_MS } from '@/lib/fetch-with-timeout'
import { createSyncable, RowBase } from '@/lib/sync-manager/helpers.ts'
import {
  comparePullCheckpoints,
  initialPullCheckpoint,
  pullFromServer,
  type PullCheckpoint,
} from '@/lib/sync-manager/pull-checkpoint'
import { pushToServer } from '@/lib/sync-manager/push-to-server'
import { reportSyncFailure } from '@/lib/sync-manager/report-sync-failure'
import { syncFailureReason } from '@/lib/sync-manager/sync-errors'
import { trpc } from '@/clients/trpc.ts'
import type { DexieDatabase, InterventoDoc } from '@/db/local/dexie-db.ts'
import { readyToSend, repairIdsOf } from '@/features/riparazioni/proof-photos'

type FotoInterventoRow = FotoIntervento & { blob?: File } & RowBase

const pushFotoInterventi = pushToServer<FotoInterventoRow>('foto_interventi')

async function uploadToS3(record: FotoInterventoRow) {
  const { signedUrl } = await trpc.utils.generateSignedUrl.query({
    key: record.id,
    method: 'put',
  })
  const res = await fetchWithTimeout(
    signedUrl,
    { method: 'PUT', body: record.blob },
    UPLOAD_TIMEOUT_MS
  )
  if (!res.ok) throw new Error('Failed to upload to S3')
}

/**
 * Marks the photo as uploaded on the device (`s3_key`), unless it changed since
 * the push read it (a replaced photo has a new file to upload). Written as the
 * server's would be: the row stays as unsent as it was, with the same version.
 */
async function recordUpload(db: DexieDatabase, record: FotoInterventoRow) {
  await db.transaction('rw', db.foto_interventi, async (transaction) => {
    transaction.ctx = { fromServer: true }
    const local: FotoInterventoRow | undefined = await db.foto_interventi.get(
      record.id
    )
    if (local && !local.s3_key && local._rev === record._rev)
      await db.foto_interventi.update(record.id, { s3_key: record.id })
  })
}

export const createFotoInterventiSyncable = (db: DexieDatabase) =>
  createSyncable<FotoInterventoRow, PullCheckpoint | Date>({
    db,
    name: 'foto_interventi',
    push: {
      batchSize: 200,
      // Proof photos wait for their repair to reach the server: this round
      // also runs after a failed interventi round, and alone on a realtime
      // change. Only their interventi are read, each folded once
      holdBack: async (rows) => {
        const proofs = rows.filter((row) => row.riparazione_id)
        if (!proofs.length) return []
        const ids = [...new Set(proofs.map((row) => row.intervento_id ?? ''))]
        const interventi = new Map(
          (await db.interventi.bulkGet(ids)).flatMap((doc) =>
            doc ? [[doc.id, doc] as const] : []
          )
        )
        const repairIds = memoize(repairIdsOf, (doc: InterventoDoc) => doc.id)
        return proofs.filter(
          (row) =>
            !readyToSend(
              row,
              interventi.get(row.intervento_id ?? ''),
              repairIds
            )
        )
      },
      handler: async (rows) => {
        const orderedRows = orderBy(rows, ['updated_at'], ['asc'])

        // One upload at a time: on a weak uplink, parallel uploads and the
        // retries started while they still run compete and time out together
        // (K42). Each upload is recorded on the device as it finishes, so a
        // failure later in the batch doesn't send it again next time.
        const processedRows: FotoInterventoRow[] = []
        const errors: unknown[] = []
        for (const record of orderedRows) {
          if (!record.blob || record.s3_key) {
            processedRows.push(record)
            continue
          }
          try {
            await uploadToS3(record)
          } catch (err) {
            reportSyncFailure('upload-foto-intervento-to-s3', err)
            errors.push(err)
            // A timeout says the link can't carry a photo now: stop. Any other
            // failure is this photo's own (a refused URL, an unreadable
            // file): the photos after it still go
            if (syncFailureReason(err) === 'timeout') break
            continue
          }
          await recordUpload(db, record)
          processedRows.push({ ...record, s3_key: record.id })
        }

        // The photos that made it are pushed even when one failed: they are
        // the ones sent, the failed ones stay unsent and the sync fails
        const { records } = processedRows.length
          ? await pushFotoInterventi(processedRows)
          : { records: [] }

        const checkpoint = last(orderedRows)?.updated_at

        return errors.length
          ? { checkpoint, records, sent: processedRows, error: errors[0] }
          : { checkpoint, records }
      },
    },

    pull: {
      batchSize: 1200,
      initialCheckpoint: initialPullCheckpoint,
      compareCheckpoints: comparePullCheckpoints,
      handler: pullFromServer<FotoInterventoRow>('foto_interventi'),
    },
  })
